import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { useRef } from 'react';
import { customStylesModal } from './ConfirmModal';
import { Button } from './Button';
import { ButtonVariant } from '../config/colors';
import { connectionRepository } from '../repositories/ConnectionRepository';
import { useAuth } from '../hooks/useAuth';
import { useConfirm } from '../hooks/useConfirm';
import { useTheme } from '../hooks/useTheme';
import { isAdmin, isSubscriptionLifetime } from '../models/Pleb';

interface ConnectDialogProps {}

const ConnectDialog: React.FC<ConnectDialogProps> = () => {
  const [message, setMessage] = useState('');
  const [sendMessageEnabled, setSendMessageEnabled] = useState(false);
  const [sending, setSending] = useState(false);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const { pleb } = useAuth();
  const { connectDialogIsOpen, connectDialogData, openConfirm, closeConnectDialog } = useConfirm();
  const userToConnect = connectDialogData;
  const {isMobile} = useTheme();

  useEffect(() => {
    if (connectDialogIsOpen) {
        if(textAreaRef.current) {
            textAreaRef.current.focus();
        }
    }
  }, [connectDialogIsOpen]);

  useEffect(() => {
    const messageText = message.trim();
    const containsUrl = /https?:\/\/[^\s]+/.test(messageText);
    const isLifetimeMember = isSubscriptionLifetime(pleb?.subscription ?? '');
    const isAdminUser = pleb ? isAdmin(pleb) : false;
    const shouldBlockUrl = !isLifetimeMember && !isAdminUser;
    setSendMessageEnabled(messageText.length > 0 && (!shouldBlockUrl || !containsUrl));
  }, [message, pleb]);

  const handleSendMessage = async () => {
    setSending(true);
    try {
        if(pleb?.id && userToConnect?.id)
        {
            await connectionRepository.createConversation({
                userId: userToConnect.id,
                text: message,
                currentUserId: pleb.id
            });

            closeConnectDialog();

            openConfirm({
                title: "Message sent!",
                message: "We will let you know when " + userToConnect.name + " reply",
                buttons: [
                    { label: "Thanks", onClick: () => {}, variant: ButtonVariant.inverted }, 
                ],
            });

            setMessage('');
        }
    } finally {
      setSending(false);
    }
  };

  const renderFeatureItem = (children: () => React.JSX.Element) => {
    return (
        <div className="flex items-center gap-3 mb-3">
            <i className="material-icons text-brand-orange">check</i>
            <div 
                className="flex-1 text-onBackground text-base"
            >
                {children()}
            </div>
        </div>
    )
  };

  return (
    <Modal
      isOpen={connectDialogIsOpen}
      onRequestClose={closeConnectDialog}
      style={customStylesModal('440px', isMobile ? true : false)}
    >
      <div className="p-4 flex flex-col">
        <h2 className="text-center text-xl">
          Connect with <strong>{userToConnect?.name}</strong>
        </h2>

        <div className="mt-6">
          <textarea
            ref={textAreaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="w-full bg-brand-bg-1 text-onBackground p-4 rounded-2xl min-h-[120px] resize-none border border-brand-border-color"
            placeholder="This could be the start of a great friendship..."
          />
        </div>

        <div className="mt-3">
            {renderFeatureItem(() => <div>You can only send <span className="font-bold">{userToConnect?.name} </span> 1 message request</div>)}
            {renderFeatureItem(() => <div>If <span className="font-bold">{userToConnect?.name}</span> replies, you will be able to chat more</div>)}
        </div>

        <div className="mt-8">
            <Button
                variant={ButtonVariant.inverted}
                onClick={handleSendMessage}
                disabled={!sendMessageEnabled && !sending}
            >
                Send Message
            </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ConnectDialog; 